import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Gender, RelationshipType } from '@/types/enums';
import { X, Plus, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useDebounce } from '@/hooks/useDebounce';
import { hasRegistrationFormInput } from '@/utils/registrationHelpers';
import { validators } from '@/utils/validators';
import { sanitize } from '@/utils/sanitize';
import { logger } from '@/utils/logger';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  MONTHS,
  LANGUAGES,
  HEAR_ABOUT_OPTIONS,
  MAX_ADULTS,
  MAX_CHILDREN,
  generateAdultYears,
  generateChildYears,
  generatePregnancyYears,
} from '@/constants/formOptions';
interface AdultFormData {
  id: string;
  firstName: string;
  lastName: string;
  birthMonth: string;
  birthYear: string;
  phone: string;
  email: string;
  gender: Gender | '';
  relationshipToChild: RelationshipType | '';
}
interface ChildFormData {
  id: string;
  firstName: string;
  lastName: string;
  birthMonth: string;
  birthYear: string;
  gender: Gender | '';
}
const currentYear = new Date().getFullYear();
const YEARS_ADULT = generateAdultYears(currentYear);
const YEARS_CHILD = generateChildYears(currentYear);
const YEARS_PREGNANCY = generatePregnancyYears(currentYear);
const REGISTRATION_DRAFT_KEY = 'frc_registration_draft';
export function RegistrationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('registration');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isInitialLoadRef = useRef(true);
  const hasPrefilledFromUser = useRef(false);
  const [adults, setAdults] = useState<AdultFormData[]>([
    {
      id: crypto.randomUUID(),
      firstName: '',
      lastName: '',
      birthMonth: '',
      birthYear: '',
      phone: '',
      email: '',
      gender: '',
      relationshipToChild: '',
    },
  ]);
  const [children, setChildren] = useState<ChildFormData[]>([]);
  const [isPregnant, setIsPregnant] = useState(false);
  const [pregnantAdultId, setPregnantAdultId] = useState<string>('');
  const [dueMonth, setDueMonth] = useState('');
  const [dueYear, setDueYear] = useState('');
  const [numberOfBabies, setNumberOfBabies] = useState(1);
  const [homePostalCode, setHomePostalCode] = useState('');
  const [isLowIncome, setIsLowIncome] = useState<boolean | null>(null);
  const [languagesSpoken, setLanguagesSpoken] = useState<string[]>([]);
  const [otherLanguage, setOtherLanguage] = useState('');
  const [hearAboutUs, setHearAboutUs] = useState<string[]>([]);
  const [hearAboutUsOther, setHearAboutUsOther] = useState('');
  const [referralDetails, setReferralDetails] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [formErrors, setFormErrors] = useState<Map<string, string>>(new Map());
  const scrollToError = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => element.focus(), 100);
    }
  };
  const getErrorClass = (id: string) =>
    formErrors.has(id) ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50/10' : '';
  const getErrorMessage = (id: string) => formErrors.get(id) || '';
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(REGISTRATION_DRAFT_KEY);
      logger.log('Checking for saved draft...', savedDraft ? 'Found!' : 'Not found');
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        logger.log('Restoring draft:', draft);
        if (hasRegistrationFormInput(draft)) {
          if (draft.adults !== undefined) {
            const adultsWithEmail = draft.adults.map((adult: AdultFormData) => ({
              ...adult,
              email: adult.email || '',
            }));
            setAdults(adultsWithEmail);
          }
          if (draft.children !== undefined) setChildren(draft.children);
          if (draft.homePostalCode !== undefined) setHomePostalCode(sanitize.postalCode(draft.homePostalCode));
          if (draft.isLowIncome !== undefined) setIsLowIncome(draft.isLowIncome);
          if (draft.isPregnant !== undefined) setIsPregnant(draft.isPregnant);
          if (draft.pregnantAdultId !== undefined) setPregnantAdultId(draft.pregnantAdultId);
          if (draft.dueMonth !== undefined) setDueMonth(draft.dueMonth);
          if (draft.dueYear !== undefined) setDueYear(draft.dueYear);
          if (draft.numberOfBabies !== undefined) setNumberOfBabies(draft.numberOfBabies);
          if (draft.languagesSpoken !== undefined) setLanguagesSpoken(draft.languagesSpoken);
          if (draft.otherLanguage !== undefined) setOtherLanguage(draft.otherLanguage);
          if (draft.hearAboutUs !== undefined) setHearAboutUs(draft.hearAboutUs);
          if (draft.hearAboutUsOther !== undefined) setHearAboutUsOther(draft.hearAboutUsOther);
          if (draft.referralDetails !== undefined) setReferralDetails(draft.referralDetails);
          if (draft.privacyPolicyAccepted !== undefined) setPrivacyPolicyAccepted(draft.privacyPolicyAccepted);
          toast.success(t('toast.draftRestored'));
        } else {
          localStorage.removeItem(REGISTRATION_DRAFT_KEY);
          logger.log('Removed empty draft from localStorage');
        }
      }
      isInitialLoadRef.current = false;
    } catch (error) {
      logger.error('Error loading draft:', error);
      isInitialLoadRef.current = false;
    }
  }, []);
  useEffect(() => {
    if (user && !hasPrefilledFromUser.current) {
      hasPrefilledFromUser.current = true;
      setAdults((prev) => {
        const updatedAdults = [...prev];
        if (updatedAdults.length > 0) {
          const primaryAdult = updatedAdults[0];
          updatedAdults[0] = {
            ...primaryAdult,
            firstName: primaryAdult.firstName || user.firstName || '',
            lastName: primaryAdult.lastName || user.lastName || '',
            email: user.email || primaryAdult.email || '',
          };
        }
        return updatedAdults;
      });
    }
  }, [user]);
  const formDataSnapshot = useMemo(
    () => ({
      adults,
      children,
      homePostalCode,
      isLowIncome,
      isPregnant,
      pregnantAdultId,
      dueMonth,
      dueYear,
      numberOfBabies,
      languagesSpoken,
      otherLanguage,
      hearAboutUs,
      hearAboutUsOther,
      referralDetails,
      privacyPolicyAccepted,
    }),
    [
      adults,
      children,
      homePostalCode,
      isLowIncome,
      isPregnant,
      pregnantAdultId,
      dueMonth,
      dueYear,
      numberOfBabies,
      languagesSpoken,
      otherLanguage,
      hearAboutUs,
      hearAboutUsOther,
      referralDetails,
      privacyPolicyAccepted,
    ],
  );
  const debouncedFormData = useDebounce(formDataSnapshot, 1000);
  useEffect(() => {
    if (isInitialLoadRef.current) {
      return;
    }
    const formData = {
      ...debouncedFormData,
      _savedAt: new Date().toISOString(),
      _version: '1.0',
    };
    try {
      localStorage.setItem(REGISTRATION_DRAFT_KEY, JSON.stringify(formData));
      logger.log('Draft auto-saved at:', formData._savedAt);
    } catch (error) {
      logger.error('Error saving draft:', error);
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        toast.error(t('toast.storageFull'));
      }
    }
  }, [debouncedFormData]);
  const handleAdultChange = (id: string, field: keyof AdultFormData, value: string) => {
    setAdults((prev) => prev.map((adult) => (adult.id === id ? { ...adult, [field]: value } : adult)));
  };
  const handleChildChange = (id: string, field: keyof ChildFormData, value: string) => {
    setChildren((prev) => prev.map((child) => (child.id === id ? { ...child, [field]: value } : child)));
  };
  const addAdult = () => {
    if (adults.length < MAX_ADULTS) {
      setAdults((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          firstName: '',
          lastName: '',
          birthMonth: '',
          birthYear: '',
          phone: '',
          email: '',
          gender: '',
          relationshipToChild: '',
        },
      ]);
    }
  };
  const removeAdult = (id: string) => {
    if (adults.length > 1) {
      setAdults((prev) => prev.filter((adult) => adult.id !== id));
    }
  };
  const addChild = () => {
    if (children.length < MAX_CHILDREN) {
      setChildren((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          firstName: '',
          lastName: '',
          birthMonth: '',
          birthYear: '',
          gender: '',
        },
      ]);
    }
  };
  const removeChild = (id: string) => {
    setChildren((prev) => prev.filter((child) => child.id !== id));
  };
  const handleLanguageToggle = (language: string) => {
    setLanguagesSpoken((prev) => {
      const isRemoving = prev.includes(language);
      if (isRemoving && language === 'Other') {
        setOtherLanguage('');
      }
      return isRemoving ? prev.filter((lang) => lang !== language) : [...prev, language];
    });
  };
  const validateForm = (): {
    isValid: boolean;
    firstErrorId?: string;
  } => {
    const errors = new Map<string, string>();
    let firstErrorId: string | undefined;
    const addError = (id: string, message: string) => {
      errors.set(id, message);
      if (!firstErrorId) {
        firstErrorId = id;
      }
    };
    for (let i = 0; i < adults.length; i++) {
      const adult = adults[i];
      const label = i === 0 ? t('labels.primaryAdult') : t('labels.adult', { index: i + 1 });
      if (!adult.firstName.trim()) {
        addError(`adult-${adult.id}-firstName`, t('validation.enterNameFor', { label }));
      } else if (!adult.lastName.trim()) {
        addError(`adult-${adult.id}-lastName`, t('validation.enterNameFor', { label }));
      }
      if (!adult.birthMonth) {
        addError(`adult-${adult.id}-birthMonth`, t('validation.enterBirthFor', { label }));
      } else if (!adult.birthYear) {
        addError(`adult-${adult.id}-birthYear`, t('validation.enterBirthFor', { label }));
      }
      const phoneValidation = validators.validatePhone(adult.phone);
      if (!phoneValidation.isValid) {
        addError(`adult-${adult.id}-phone`, t('validation.phoneError', { label, error: phoneValidation.error }));
      }
      if (i === 0) {
        if (!adult.email.trim()) {
          addError(`adult-${adult.id}-email`, t('validation.emailRequired', { label }));
        } else {
          const emailValidation = validators.validateEmail(adult.email);
          if (!emailValidation.isValid) {
            addError(`adult-${adult.id}-email`, t('validation.emailError', { label, error: emailValidation.error }));
          }
        }
      } else if (adult.email.trim()) {
        const emailValidation = validators.validateEmail(adult.email);
        if (!emailValidation.isValid) {
          addError(`adult-${adult.id}-email`, t('validation.emailError', { label, error: emailValidation.error }));
        }
      }
      if (!adult.gender) {
        addError(`adult-${adult.id}-gender`, t('validation.selectGenderFor', { label }));
      }
      if (!adult.relationshipToChild) {
        addError(`adult-${adult.id}-relationship`, t('validation.selectRelationshipFor', { label }));
      }
    }
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (!child.firstName.trim()) {
        addError(`child-${child.id}-firstName`, t('validation.enterChildName', { index: i + 1 }));
      } else if (!child.lastName.trim()) {
        addError(`child-${child.id}-lastName`, t('validation.enterChildName', { index: i + 1 }));
      }
      if (!child.birthMonth) {
        addError(`child-${child.id}-birthMonth`, t('validation.enterChildBirth', { index: i + 1 }));
      } else if (!child.birthYear) {
        addError(`child-${child.id}-birthYear`, t('validation.enterChildBirth', { index: i + 1 }));
      }
      if (!child.gender) {
        addError(`child-${child.id}-gender`, t('validation.selectChildGender', { index: i + 1 }));
      } else {
        const childAge = currentYear - parseInt(child.birthYear);
        if (childAge > 18) {
          addError(`child-${child.id}-birthYear`, t('validation.childAgeLimit', { index: i + 1 }));
        }
      }
    }
    if (isPregnant) {
      if (numberOfBabies === undefined || numberOfBabies === null || numberOfBabies < 0) {
        addError('numberOfBabies', t('validation.validNumberBabies'));
      }
      if (!dueMonth) {
        addError('dueMonth', t('validation.selectDueMonth'));
      }
      if (!dueYear) {
        addError('dueYear', t('validation.selectDueYear'));
      }
      if (adults.length > 0 && !pregnantAdultId) {
        addError('pregnantAdult', t('validation.selectPregnantAdult'));
      }
    }
    const postalCodeValidation = validators.validatePostalCode(homePostalCode);
    if (!postalCodeValidation.isValid) {
      addError('homePostalCode', postalCodeValidation.error || t('validation.invalidPostalCode'));
    }
    if (languagesSpoken.length === 0) {
      addError('language-English', t('validation.selectLanguage'));
    }
    if (languagesSpoken.includes('Other') && !otherLanguage.trim()) {
      addError('otherLanguage', t('validation.specifyOtherLang'));
    }
    if (hearAboutUs.length === 0) {
      addError('hear-Friends', t('validation.letUsKnowHear'));
    }
    if (hearAboutUs.includes('Other') && !hearAboutUsOther.trim()) {
      addError('hearAboutUsOther', t('validation.specifyHowHeard'));
    }
    if (hearAboutUs.includes('Referral') && !referralDetails.trim()) {
      addError('referralDetails', t('validation.specifyReferral'));
    }
    if (!consentGiven) {
      addError('consentGiven', t('validation.provideConsent'));
    }
    if (!privacyPolicyAccepted) {
      addError('privacyPolicyAccepted', t('validation.acceptPolicy'));
    }
    setFormErrors(errors);
    return { isValid: errors.size === 0, firstErrorId };
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { isValid, firstErrorId } = validateForm();
    if (!isValid) {
      if (firstErrorId) {
        scrollToError(firstErrorId);
      }
      return;
    }
    setIsSubmitting(true);
    try {
      const registrationData = {
        adults: adults.map((adult) => ({
          ...adult,
          birthMonth: parseInt(adult.birthMonth),
          birthYear: parseInt(adult.birthYear),
        })),
        children: children.map((child) => ({
          ...child,
          birthMonth: parseInt(child.birthMonth),
          birthYear: parseInt(child.birthYear),
        })),
        pregnancy: {
          isPregnant: isPregnant,
          expectedDeliveryMonth: isPregnant ? parseInt(dueMonth) : null,
          expectedDeliveryYear: isPregnant ? parseInt(dueYear) : null,
          expectedBabies: isPregnant ? numberOfBabies : null,
          adultIndex: isPregnant
            ? Math.max(
                0,
                adults.findIndex((a) => a.id === pregnantAdultId),
              )
            : 0,
        },
        homePostalCode: homePostalCode,
        isLowIncome: isLowIncome,
        languagesSpoken: languagesSpoken,
        otherLanguage: languagesSpoken.includes('Other') ? otherLanguage : null,
        hearAboutUs: hearAboutUs,
        hearAboutUsOther: hearAboutUs.includes('Other') ? hearAboutUsOther : null,
        referralDetails: hearAboutUs.includes('Referral') ? referralDetails : null,
        consentGiven: consentGiven,
        consentDate: new Date().toISOString(),
      };
      logger.log('Registration Data prepared for next step:', registrationData);
      navigate('/photo-release', { state: { registrationData } });
      toast.success(t('toast.progressSaved'));
    } catch (error) {
      logger.error('Error proceeding to next step:', error);
      toast.error(t('toast.failedProceed'));
    } finally {
      setIsSubmitting(false);
    }
  };
  const hasUserInput = () =>
    hasRegistrationFormInput({
      adults,
      children,
      homePostalCode,
      isLowIncome,
      isPregnant,
      dueMonth,
      dueYear,
      numberOfBabies,
      languagesSpoken,
      otherLanguage,
      hearAboutUs,
      hearAboutUsOther,
      referralDetails,
      privacyPolicyAccepted,
    });
  const handleBackClick = () => {
    if (hasUserInput()) {
      if (window.confirm(t('dialogs.unsavedChanges'))) {
        navigate('/my-families');
      }
    } else {
      navigate('/my-families');
    }
  };
  const handleClearDraft = () => {
    if (window.confirm(t('dialogs.confirmClearDraft'))) {
      localStorage.removeItem(REGISTRATION_DRAFT_KEY);
      setAdults([
        {
          id: crypto.randomUUID(),
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          birthMonth: '',
          birthYear: '',
          phone: '',
          email: user?.email || '',
          gender: '',
          relationshipToChild: '',
        },
      ]);
      setChildren([]);
      setHomePostalCode('');
      setIsLowIncome(null);
      setIsPregnant(false);
      setDueMonth('');
      setDueYear('');
      setNumberOfBabies(1);
      setLanguagesSpoken([]);
      setOtherLanguage('');
      setHearAboutUs([]);
      setHearAboutUsOther('');
      setReferralDetails('');
      setConsentGiven(false);
      setPrivacyPolicyAccepted(false);
      setPregnantAdultId('');
      toast.success(t('toast.draftCleared'));
    }
  };
  useEffect(() => {
    if (isPregnant && !pregnantAdultId && adults.length > 0) {
      setPregnantAdultId(adults[0].id);
    }
  }, [isPregnant, adults]);
  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-yellow-50 via-pink-50 to-blue-50 px-3 py-4 sm:px-4 sm:py-8 md:px-6 lg:px-8 landscape:py-2 landscape:sm:py-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg md:rounded-xl shadow-xl p-4 sm:p-6 md:p-8 landscape:p-4">
          <div className="relative mb-4 sm:mb-6">
            <div className="absolute left-0 top-0">
              <Button
                type="button"
                onClick={handleBackClick}
                className="touch-feedback bg-purple-600 hover:bg-purple-700 text-white px-3 py-2.5 sm:px-4 sm:py-2.5 text-sm min-h-[44px] sm:min-h-[40px] font-medium rounded shadow-sm transition-colors duration-200"
              >
                {t('buttons.back')}
              </Button>
            </div>

            <div className="text-center pt-8 sm:pt-0">
              <div className="mb-3 landscape:mb-1">
                <img
                  src="/logo.png"
                  alt={t('page.logoAlt')}
                  className="mx-auto w-full max-w-[100px] sm:max-w-[120px] landscape:max-w-[60px] landscape:sm:max-w-[80px] h-auto"
                />
              </div>
              <h1 className="text-lg sm:text-xl md:text-2xl landscape:text-base font-bold text-gray-900 mb-1.5 landscape:mb-1">
                {t('page.title')}
              </h1>
              <p className="text-xs sm:text-sm text-gray-600">{t('page.subtitle')}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6 md:space-y-7 landscape:space-y-3">
            {formErrors.size > 0 && (
              <div
                id="error-banner"
                className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 rounded-r-lg mb-4 shadow-md sticky top-0 z-10"
                role="alert"
                aria-live="polite"
              >
                <div className="flex gap-2 sm:gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs sm:text-sm md:text-base font-bold text-red-800 mb-2">
                      {t('validation.formHasErrors')}
                    </h3>
                    <div className="text-xs sm:text-sm text-red-700 space-y-1">
                      {Array.from(formErrors.values())
                        .slice(0, 5)
                        .map((error, idx) => (
                          <div key={idx} className="flex items-start gap-1.5 break-words">
                            <span className="text-red-500 font-bold mt-0.5">•</span>
                            <span>{error}</span>
                          </div>
                        ))}
                      {formErrors.size > 5 && (
                        <div className="font-semibold mt-2 text-red-800">
                          {t('validation.andMore', { count: formErrors.size - 5 })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">{t('sections.adults')}</h2>
                {adults.length < 5 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addAdult}
                    className="touch-feedback flex items-center gap-1 text-xs sm:text-sm px-3 py-2.5 sm:px-4 sm:py-2.5 min-h-[44px] sm:min-h-[40px] hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700 transition-colors duration-200"
                  >
                    <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">{t('buttons.addAdult')}</span>
                    <span className="sm:hidden">{t('buttons.add')}</span>
                  </Button>
                )}
              </div>

              {adults.map((adult, index) => (
                <div
                  key={adult.id}
                  className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-5 landscape:p-3 space-y-3 sm:space-y-4 relative"
                >
                  {adults.length > 1 && index > 0 && (
                    <button
                      type="button"
                      onClick={() => removeAdult(adult.id)}
                      className="absolute top-2 right-2 p-3 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                      aria-label={t('ariaLabels.removeAdult')}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}

                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-700">
                      {index === 0 ? t('labels.primaryAdult') : t('labels.adult', { index: index + 1 })}
                    </h3>
                    {index === 0 && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        {t('labels.primary')}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor={`adult-${adult.id}-firstName`} className="min-h-[24px] flex items-end">
                        {t('labels.firstName')}
                      </Label>
                      <Input
                        id={`adult-${adult.id}-firstName`}
                        value={adult.firstName}
                        onChange={(e) => handleAdultChange(adult.id, 'firstName', e.target.value)}
                        className={`mt-1 ${getErrorClass(`adult-${adult.id}-firstName`)}`}
                      />
                      {getErrorMessage(`adult-${adult.id}-firstName`) && (
                        <div className="mt-1 flex items-start gap-1.5">
                          <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-red-600">{getErrorMessage(`adult-${adult.id}-firstName`)}</p>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label htmlFor={`adult-${adult.id}-lastName`} className="min-h-[24px] flex items-end">
                        {t('labels.lastName')}
                      </Label>
                      <Input
                        id={`adult-${adult.id}-lastName`}
                        value={adult.lastName}
                        onChange={(e) => handleAdultChange(adult.id, 'lastName', e.target.value)}
                        className={`mt-1 ${getErrorClass(`adult-${adult.id}-lastName`)}`}
                      />
                      {getErrorMessage(`adult-${adult.id}-lastName`) && (
                        <div className="mt-1 flex items-start gap-1.5">
                          <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-red-600">{getErrorMessage(`adult-${adult.id}-lastName`)}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor={`adult-${adult.id}-birthMonth`} className="min-h-[24px] flex items-end">
                          {t('labels.birthMonth')}
                        </Label>
                        <select
                          id={`adult-${adult.id}-birthMonth`}
                          value={adult.birthMonth}
                          onChange={(e) => handleAdultChange(adult.id, 'birthMonth', e.target.value)}
                          className={`mt-1 w-full rounded-md border border-gray-300 px-3 py-2.5 sm:py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${getErrorClass(`adult-${adult.id}-birthMonth`)}`}
                          style={{ fontSize: '16px' }}
                        >
                          <option value="">{t('placeholders.selectMonth')}</option>
                          {MONTHS.map((month) => (
                            <option key={month.value} value={month.value}>
                              {month.label}
                            </option>
                          ))}
                        </select>
                        {getErrorMessage(`adult-${adult.id}-birthMonth`) && (
                          <p className="mt-1 text-sm text-red-600">{getErrorMessage(`adult-${adult.id}-birthMonth`)}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor={`adult-${adult.id}-birthYear`} className="min-h-[24px] flex items-end">
                          {t('labels.birthYear')}
                        </Label>
                        <select
                          id={`adult-${adult.id}-birthYear`}
                          value={adult.birthYear}
                          onChange={(e) => handleAdultChange(adult.id, 'birthYear', e.target.value)}
                          className={`mt-1 w-full rounded-md border border-gray-300 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${getErrorClass(`adult-${adult.id}-birthYear`)}`}
                          style={{ fontSize: '16px' }}
                        >
                          <option value="">{t('placeholders.selectYear')}</option>
                          {YEARS_ADULT.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor={`adult-${adult.id}-phone`} className="min-h-[24px] flex items-end">
                          {t('labels.phoneNumber')}
                        </Label>
                        <Input
                          id={`adult-${adult.id}-phone`}
                          type="tel"
                          value={adult.phone}
                          onChange={(e) => handleAdultChange(adult.id, 'phone', e.target.value)}
                          placeholder={t('placeholders.phone')}
                          className={`mt-1 ${getErrorClass(`adult-${adult.id}-phone`)}`}
                        />
                        {getErrorMessage(`adult-${adult.id}-phone`) && (
                          <p className="mt-1 text-sm text-red-600">{getErrorMessage(`adult-${adult.id}-phone`)}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor={`adult-${adult.id}-email`} className="min-h-[24px] flex items-end">
                          {t('labels.emailAddress', { required: index === 0 ? ' *' : '' })}
                        </Label>
                        <Input
                          id={`adult-${adult.id}-email`}
                          type="email"
                          value={adult.email || ''}
                          onChange={(e) => handleAdultChange(adult.id, 'email', e.target.value)}
                          placeholder={t('placeholders.email')}
                          disabled={index === 0 && !!user?.email}
                          className={`mt-1 ${index === 0 && user?.email ? 'bg-gray-100 cursor-not-allowed' : ''} ${getErrorClass(`adult-${adult.id}-email`)}`}
                        />
                        {index === 0 && user?.email && (
                          <p className="text-xs text-gray-500 mt-1">{t('hints.emailLinked')}</p>
                        )}
                        {getErrorMessage(`adult-${adult.id}-email`) && (
                          <p className="mt-1 text-sm text-red-600">{getErrorMessage(`adult-${adult.id}-email`)}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor={`adult-${adult.id}-gender`} className="min-h-[24px] flex items-end">
                          {t('labels.gender')}
                        </Label>
                        <select
                          id={`adult-${adult.id}-gender`}
                          value={adult.gender}
                          onChange={(e) => handleAdultChange(adult.id, 'gender', e.target.value as Gender)}
                          className={`mt-1 w-full rounded-md border border-gray-300 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${getErrorClass(`adult-${adult.id}-gender`)}`}
                          style={{ fontSize: '16px' }}
                        >
                          <option value="">{t('placeholders.selectGender')}</option>
                          <option value={Gender.MALE}>{t('genderOptions.male')}</option>
                          <option value={Gender.FEMALE}>{t('genderOptions.female')}</option>
                          <option value={Gender.OTHER}>{t('genderOptions.other')}</option>
                          <option value={Gender.PREFER_NOT_TO_SAY}>{t('genderOptions.preferNotToSay')}</option>
                        </select>
                        {getErrorMessage(`adult-${adult.id}-gender`) && (
                          <p className="mt-1 text-sm text-red-600">{getErrorMessage(`adult-${adult.id}-gender`)}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor={`adult-${adult.id}-relationship`} className="min-h-[24px] flex items-end">
                          {t('labels.relationshipToChild')}
                        </Label>
                        <select
                          id={`adult-${adult.id}-relationship`}
                          value={adult.relationshipToChild}
                          onChange={(e) =>
                            handleAdultChange(adult.id, 'relationshipToChild', e.target.value as RelationshipType)
                          }
                          className={`mt-1 w-full rounded-md border border-gray-300 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${getErrorClass(`adult-${adult.id}-relationship`)}`}
                          style={{ fontSize: '16px' }}
                        >
                          <option value="">{t('placeholders.selectRelationship')}</option>
                          <option value={RelationshipType.PARENT}>{t('relationshipOptions.parent')}</option>
                          <option value={RelationshipType.GRANDPARENT}>{t('relationshipOptions.grandparent')}</option>
                          <option value={RelationshipType.RELATIVE}>{t('relationshipOptions.relative')}</option>
                          <option value={RelationshipType.AUTHORIZED}>
                            {t('relationshipOptions.authorizedGuardian')}
                          </option>
                          <option value={RelationshipType.EMERGENCY}>{t('relationshipOptions.emergency')}</option>
                        </select>
                        {getErrorMessage(`adult-${adult.id}-relationship`) && (
                          <p className="mt-1 text-sm text-red-600">
                            {getErrorMessage(`adult-${adult.id}-relationship`)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">
                  {t('sections.children')}
                </h2>
                {children.length < 5 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addChild}
                    className="touch-feedback flex items-center gap-1 text-xs sm:text-sm px-3 py-2.5 sm:px-4 sm:py-2.5 min-h-[44px] sm:min-h-[40px] hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700 transition-colors duration-200"
                  >
                    <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">{t('buttons.addChild')}</span>
                    <span className="sm:hidden">{t('buttons.add')}</span>
                  </Button>
                )}
              </div>

              {children.length === 0 && (
                <div className="border border-gray-200 rounded-lg p-6 text-center">
                  <p className="text-gray-600 text-sm sm:text-base">{t('misc.noChildrenAdded')}</p>
                  <p className="text-gray-500 text-xs sm:text-sm mt-2">{t('misc.childrenOptional')}</p>
                </div>
              )}

              {children.map((child, index) => (
                <div
                  key={child.id}
                  className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-5 landscape:p-3 space-y-3 sm:space-y-4 relative"
                >
                  <button
                    type="button"
                    onClick={() => removeChild(child.id)}
                    className="absolute top-2 right-2 p-3 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                    aria-label={t('ariaLabels.removeChild')}
                  >
                    <X className="h-5 w-5" />
                  </button>

                  <h3 className="text-sm font-medium text-gray-700">{t('labels.child', { index: index + 1 })}</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor={`child-${child.id}-firstName`} className="min-h-[24px] flex items-end">
                        {t('labels.firstName')}
                      </Label>
                      <Input
                        id={`child-${child.id}-firstName`}
                        value={child.firstName}
                        onChange={(e) => handleChildChange(child.id, 'firstName', e.target.value)}
                        className={`mt-1 ${getErrorClass(`child-${child.id}-firstName`)}`}
                      />
                      {getErrorMessage(`child-${child.id}-firstName`) && (
                        <p className="mt-1 text-sm text-red-600">{getErrorMessage(`child-${child.id}-firstName`)}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor={`child-${child.id}-lastName`} className="min-h-[24px] flex items-end">
                        {t('labels.lastName')}
                      </Label>
                      <Input
                        id={`child-${child.id}-lastName`}
                        value={child.lastName}
                        onChange={(e) => handleChildChange(child.id, 'lastName', e.target.value)}
                        className={`mt-1 ${getErrorClass(`child-${child.id}-lastName`)}`}
                      />
                      {getErrorMessage(`child-${child.id}-lastName`) && (
                        <p className="mt-1 text-sm text-red-600">{getErrorMessage(`child-${child.id}-lastName`)}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor={`child-${child.id}-birthMonth`} className="min-h-[24px] flex items-end">
                        {t('labels.birthMonth')}
                      </Label>
                      <select
                        id={`child-${child.id}-birthMonth`}
                        value={child.birthMonth}
                        onChange={(e) => handleChildChange(child.id, 'birthMonth', e.target.value)}
                        className={`mt-1 w-full rounded-md border border-gray-300 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${getErrorClass(`child-${child.id}-birthMonth`)}`}
                        style={{ fontSize: '16px' }}
                      >
                        <option value="">{t('placeholders.selectMonth')}</option>
                        {MONTHS.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      {getErrorMessage(`child-${child.id}-birthMonth`) && (
                        <p className="mt-1 text-sm text-red-600">{getErrorMessage(`child-${child.id}-birthMonth`)}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor={`child-${child.id}-birthYear`} className="min-h-[24px] flex items-end">
                        {t('labels.birthYear')}
                      </Label>
                      <select
                        id={`child-${child.id}-birthYear`}
                        value={child.birthYear}
                        onChange={(e) => handleChildChange(child.id, 'birthYear', e.target.value)}
                        className={`mt-1 w-full rounded-md border border-gray-300 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${getErrorClass(`child-${child.id}-birthYear`)}`}
                        style={{ fontSize: '16px' }}
                      >
                        <option value="">{t('placeholders.selectYear')}</option>
                        {YEARS_CHILD.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                      {getErrorMessage(`child-${child.id}-birthYear`) && (
                        <p className="mt-1 text-sm text-red-600">{getErrorMessage(`child-${child.id}-birthYear`)}</p>
                      )}
                    </div>

                    <div className="sm:col-span-2 md:col-span-1">
                      <Label htmlFor={`child-${child.id}-gender`} className="min-h-[24px] flex items-end">
                        {t('labels.gender')}
                      </Label>
                      <select
                        id={`child-${child.id}-gender`}
                        value={child.gender}
                        onChange={(e) => handleChildChange(child.id, 'gender', e.target.value as Gender)}
                        className={`mt-1 w-full rounded-md border border-gray-300 px-3 py-2.5 sm:py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${getErrorClass(`child-${child.id}-gender`)}`}
                        style={{ fontSize: '16px' }}
                      >
                        <option value="">{t('placeholders.selectGender')}</option>
                        <option value={Gender.MALE}>{t('genderOptions.male')}</option>
                        <option value={Gender.FEMALE}>{t('genderOptions.female')}</option>
                        <option value={Gender.OTHER}>{t('genderOptions.other')}</option>
                        <option value={Gender.PREFER_NOT_TO_SAY}>{t('genderOptions.preferNotToSay')}</option>
                      </select>
                      {getErrorMessage(`child-${child.id}-gender`) && (
                        <p className="mt-1 text-sm text-red-600">{getErrorMessage(`child-${child.id}-gender`)}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="border-b pb-2">
                <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">
                  {t('sections.pregnancy')}
                </h2>
              </div>

              <div className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-5 lg:p-6 landscape:p-3 space-y-3 sm:space-y-4">
                <label
                  htmlFor="isPregnant"
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all min-h-[56px] ${
                    isPregnant
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    id="isPregnant"
                    checked={isPregnant}
                    onChange={(e) => setIsPregnant(e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${isPregnant ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                  >
                    {isPregnant && (
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-base font-medium text-gray-900 leading-tight">
                    {t('checkboxes.joinPregnancyProgram')}
                  </span>
                </label>

                {isPregnant && (
                  <div className="space-y-3 sm:space-y-4 pl-6 sm:pl-8 pt-2">
                    <div>
                      <Label htmlFor="numberOfBabies">{t('labels.numberOfBabies')}</Label>
                      <Input
                        id="numberOfBabies"
                        type="number"
                        min="0"
                        value={numberOfBabies}
                        onChange={(e) => setNumberOfBabies(parseInt(e.target.value) || 0)}
                        className={`mt-1 ${getErrorClass('numberOfBabies')}`}
                      />
                      {getErrorMessage('numberOfBabies') && (
                        <p className="mt-1 text-sm text-red-600">{getErrorMessage('numberOfBabies')}</p>
                      )}
                    </div>

                    {adults.length > 0 && (
                      <div>
                        <Label htmlFor="pregnantAdult">{t('labels.whichAdultIsPregnant')}</Label>
                        <select
                          id="pregnantAdult"
                          value={pregnantAdultId}
                          onChange={(e) => setPregnantAdultId(e.target.value)}
                          className={`mt-1 w-full rounded-md border border-gray-300 px-3 py-2.5 sm:py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${getErrorClass('pregnantAdult')}`}
                          style={{ fontSize: '16px' }}
                        >
                          <option value="">{t('placeholders.selectAdult')}</option>
                          {adults.map((adult, index) => (
                            <option key={adult.id} value={adult.id}>
                              {adult.firstName} {adult.lastName} {index === 0 ? `(${t('labels.primary')})` : ''}
                            </option>
                          ))}
                        </select>
                        {getErrorMessage('pregnantAdult') && (
                          <p className="mt-1 text-sm text-red-600">{getErrorMessage('pregnantAdult')}</p>
                        )}
                      </div>
                    )}

                    <div>
                      <Label>{t('labels.dueDate')}</Label>
                      <div className="grid grid-cols-2 gap-2 sm:gap-4 mt-1">
                        <div>
                          <select
                            id="dueMonth"
                            value={dueMonth}
                            onChange={(e) => setDueMonth(e.target.value)}
                            className={`w-full min-h-[44px] px-3 py-2 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white ${getErrorClass('dueMonth')}`}
                          >
                            <option value="">{t('placeholders.month')}</option>
                            {MONTHS.map((month) => (
                              <option key={month.value} value={month.value}>
                                {month.label}
                              </option>
                            ))}
                          </select>
                          {getErrorMessage('dueMonth') && (
                            <div className="mt-1 flex items-start gap-1.5">
                              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-red-600">{getErrorMessage('dueMonth')}</p>
                            </div>
                          )}
                        </div>
                        <div>
                          <select
                            id="dueYear"
                            value={dueYear}
                            onChange={(e) => setDueYear(e.target.value)}
                            className={`w-full min-h-[44px] px-3 py-2 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white ${getErrorClass('dueYear')}`}
                          >
                            <option value="">{t('placeholders.year')}</option>
                            {YEARS_PREGNANCY.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                          {getErrorMessage('dueYear') && (
                            <div className="mt-1 flex items-start gap-1.5">
                              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-red-600">{getErrorMessage('dueYear')}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="border-b pb-2">
                <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">
                  {t('sections.postalCode')}
                </h2>
              </div>

              <div className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-5 lg:p-6 landscape:p-3">
                <div>
                  <Label htmlFor="homePostalCode">{t('labels.postalCodePrefix')}</Label>
                  <Input
                    id="homePostalCode"
                    type="text"
                    value={homePostalCode}
                    onChange={(e) => setHomePostalCode(e.target.value.toUpperCase())}
                    placeholder={t('placeholders.postalCode')}
                    pattern="[A-Za-z]\d[A-Za-z]"
                    maxLength={3}
                    className={`mt-1 ${getErrorClass('homePostalCode')}`}
                  />
                  <p className="text-sm text-gray-500 mt-1">{t('hints.postalCodeFormat')}</p>
                  {getErrorMessage('homePostalCode') && (
                    <p className="mt-1 text-sm text-red-600">{getErrorMessage('homePostalCode')}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="border-b pb-2">
                <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">{t('sections.income')}</h2>
              </div>

              <div className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-5 lg:p-6 landscape:p-3">
                <div className="space-y-3">
                  <div>
                    <Label className="text-base font-medium">{t('labels.lowIncome')}</Label>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">{t('hints.incomeEligibility')}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label
                      htmlFor="lowIncome-yes"
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all min-h-[56px] ${
                        isLowIncome === true
                          ? 'border-blue-500 bg-blue-50'
                          : formErrors.has('lowIncome-yes')
                            ? 'border-red-500 bg-red-50/10'
                            : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                      }`}
                    >
                      <input
                        type="radio"
                        id="lowIncome-yes"
                        name="lowIncome"
                        checked={isLowIncome === true}
                        onChange={() => setIsLowIncome(true)}
                        className="sr-only"
                      />
                      <div
                        className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${isLowIncome === true ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                      >
                        {isLowIncome === true && <div className="w-2.5 h-2.5 rounded-full bg-white"></div>}
                      </div>
                      <span className="text-sm font-medium text-gray-900 leading-tight">{t('binaryOptions.yes')}</span>
                    </label>

                    <label
                      htmlFor="lowIncome-no"
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all min-h-[56px] ${
                        isLowIncome === false
                          ? 'border-blue-500 bg-blue-50'
                          : formErrors.has('lowIncome-yes')
                            ? 'border-red-500 bg-red-50/10'
                            : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                      }`}
                    >
                      <input
                        type="radio"
                        id="lowIncome-no"
                        name="lowIncome"
                        checked={isLowIncome === false}
                        onChange={() => setIsLowIncome(false)}
                        className="sr-only"
                      />
                      <div
                        className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${isLowIncome === false ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                      >
                        {isLowIncome === false && <div className="w-2.5 h-2.5 rounded-full bg-white"></div>}
                      </div>
                      <span className="text-sm font-medium text-gray-900 leading-tight">{t('binaryOptions.no')}</span>
                    </label>
                  </div>
                  {getErrorMessage('lowIncome-yes') && (
                    <p className="mt-2 text-sm text-red-600">{getErrorMessage('lowIncome-yes')}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="border-b pb-2">
                <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">
                  {t('sections.languages')}
                </h2>
              </div>

              <div className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-5 lg:p-6 landscape:p-3">
                <div className="space-y-3">
                  <Label className="text-base md:text-lg font-medium mb-3 block">
                    {t('labels.selectAllThatApply')}
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {LANGUAGES.map((language) => (
                      <label
                        key={language}
                        htmlFor={`language-${language}`}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all min-h-[56px] ${
                          languagesSpoken.includes(language)
                            ? 'border-blue-500 bg-blue-50'
                            : formErrors.has('language-English')
                              ? 'border-red-500 bg-red-50/10'
                              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          id={`language-${language}`}
                          checked={languagesSpoken.includes(language)}
                          onChange={() => handleLanguageToggle(language)}
                          className="sr-only"
                        />
                        <div
                          className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                            languagesSpoken.includes(language)
                              ? 'bg-blue-600 border-blue-600'
                              : 'border-gray-300 bg-white'
                          }`}
                        >
                          {languagesSpoken.includes(language) && (
                            <svg
                              className="w-3.5 h-3.5 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="text-sm font-medium text-gray-900 leading-tight">
                          {t(`languageOptions.${language}`, language)}
                        </span>
                      </label>
                    ))}
                  </div>
                  {getErrorMessage('language-English') && (
                    <p className="mt-2 text-sm text-red-600">{getErrorMessage('language-English')}</p>
                  )}

                  {languagesSpoken.includes('Other') && (
                    <div className="mt-4">
                      <Label htmlFor="otherLanguage" className="text-sm font-medium">
                        {t('labels.specifyOtherLanguage')}
                      </Label>
                      <Input
                        id="otherLanguage"
                        type="text"
                        value={otherLanguage}
                        onChange={(e) => setOtherLanguage(e.target.value)}
                        placeholder={t('placeholders.enterLanguage')}
                        className={`mt-1 ${getErrorClass('otherLanguage')}`}
                      />
                      {getErrorMessage('otherLanguage') && (
                        <p className="mt-1 text-sm text-red-600">{getErrorMessage('otherLanguage')}</p>
                      )}
                    </div>
                  )}

                  {languagesSpoken.length > 0 && (
                    <div className="mt-2 p-2 bg-blue-50 rounded-md">
                      <p className="text-xs sm:text-sm text-blue-700">
                        {t('misc.selected', {
                          languages: languagesSpoken
                            .map((lang) =>
                              lang === 'Other' && otherLanguage.trim()
                                ? otherLanguage
                                : t(`languageOptions.${lang}`, lang),
                            )
                            .join(', '),
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="border-b pb-2">
                <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">
                  {t('sections.hearAboutUs')}
                </h2>
              </div>

              <div className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-5 lg:p-6 landscape:p-3">
                <div className="space-y-3">
                  <Label className="text-base font-medium">{t('labels.selectAllApply')}</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {HEAR_ABOUT_OPTIONS.map((option) => (
                      <div key={option} className="space-y-2">
                        <label
                          htmlFor={`hear-${option}`}
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all min-h-[56px] ${
                            hearAboutUs.includes(option)
                              ? 'border-blue-500 bg-blue-50'
                              : formErrors.has('hear-Friends')
                                ? 'border-red-500 bg-red-50/10'
                                : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            id={`hear-${option}`}
                            value={option}
                            checked={hearAboutUs.includes(option)}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (e.target.checked) {
                                setHearAboutUs([...hearAboutUs, value]);
                              } else {
                                setHearAboutUs(hearAboutUs.filter((item) => item !== value));
                                if (value === 'Other') {
                                  setHearAboutUsOther('');
                                }
                                if (value === 'Referral') {
                                  setReferralDetails('');
                                }
                              }
                            }}
                            className="sr-only"
                          />
                          <div
                            className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${hearAboutUs.includes(option) ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                          >
                            {hearAboutUs.includes(option) && (
                              <svg
                                className="w-3.5 h-3.5 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className="text-sm font-medium text-gray-900 leading-tight">
                            {t(`hearAboutUsOptions.${option.replace(/[\s/]/g, '')}`, option)}
                          </span>
                        </label>

                        {option === 'Referral' && hearAboutUs.includes('Referral') && (
                          <Input
                            id="referralDetails"
                            type="text"
                            value={referralDetails}
                            onChange={(e) => setReferralDetails(e.target.value)}
                            placeholder={t('placeholders.whoReferred')}
                            className="text-sm"
                          />
                        )}

                        {option === 'Other' && hearAboutUs.includes('Other') && (
                          <Input
                            id="hearAboutUsOther"
                            type="text"
                            value={hearAboutUsOther}
                            onChange={(e) => setHearAboutUsOther(e.target.value)}
                            placeholder={t('placeholders.tellUsMore')}
                            className={`text-sm ${getErrorClass('hearAboutUsOther')}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  {getErrorMessage('hear-Friends') && (
                    <p className="mt-2 text-sm text-red-600">{getErrorMessage('hear-Friends')}</p>
                  )}
                  {getErrorMessage('hearAboutUsOther') && (
                    <p className="mt-2 text-sm text-red-600">{getErrorMessage('hearAboutUsOther')}</p>
                  )}
                  {getErrorMessage('referralDetails') && (
                    <p className="mt-2 text-sm text-red-600">{getErrorMessage('referralDetails')}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center gap-2">
                <div className="h-0.5 flex-1 bg-gradient-to-r from-blue-500 to-purple-500"></div>
                <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 text-center px-2">
                  {t('sections.consent')}
                </h2>
                <div className="h-0.5 flex-1 bg-gradient-to-r from-purple-500 to-blue-500"></div>
              </div>

              <div className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-6 bg-gray-50">
                <div className="space-y-3 sm:space-y-4">
                  <div className="text-xs sm:text-sm text-gray-700 leading-relaxed space-y-2 sm:space-y-3">
                    <p>
                      <Trans i18nKey="consentText.collection" t={t}>
                        We collect personal information to improve our programs and report to our funders. This may
                        include your age, location, service use, and feedback.
                      </Trans>
                    </p>
                    <p>
                      <Trans i18nKey="consentText.confidentiality" t={t}>
                        We will <strong>not share your name or personal details</strong>. Only anonymous, grouped
                        information will be used in our reports.
                      </Trans>
                    </p>
                    <p>
                      <Trans i18nKey="consentText.voluntary" t={t}>
                        Your participation is <strong>voluntary</strong>. You can <strong>withdraw your consent</strong>{' '}
                        at any time by contacting us.
                      </Trans>
                    </p>
                  </div>

                  <label
                    htmlFor="consentGiven"
                    className={`flex items-start sm:items-center gap-3 p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-all min-h-[56px] ${
                      consentGiven
                        ? 'border-blue-500 bg-blue-50'
                        : formErrors.has('consentGiven')
                          ? 'border-red-500 bg-red-50/10'
                          : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      id="consentGiven"
                      checked={consentGiven}
                      onChange={(e) => setConsentGiven(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`flex-shrink-0 w-6 h-6 sm:w-5 sm:h-5 rounded border-2 flex items-center justify-center mt-0.5 sm:mt-0 ${consentGiven ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                    >
                      {consentGiven && (
                        <svg
                          className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-gray-900 leading-tight">
                      {t('consent.agreement')}
                    </span>
                  </label>
                  {getErrorMessage('consentGiven') && (
                    <p className="mt-2 text-sm text-red-600">{getErrorMessage('consentGiven')}</p>
                  )}

                  <div className="pt-3 sm:pt-4 border-t-2 border-gray-300">
                    <div className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-3 sm:mb-4">
                      <p>
                        {t('consent.acknowledgement')}{' '}
                        <button
                          type="button"
                          onClick={() => setShowPrivacyModal(true)}
                          className="text-blue-600 hover:text-blue-700 active:text-blue-800 underline font-semibold touch-manipulation inline-flex items-center gap-1"
                        >
                          {t('consent.privacyPolicy')}
                          <span className="text-xs">📄</span>
                        </button>
                        .
                      </p>
                    </div>

                    <div
                      className={`flex items-start sm:items-center gap-3 p-3 sm:p-4 rounded-lg border-2 transition-all min-h-[56px] ${
                        privacyPolicyAccepted
                          ? 'border-blue-500 bg-blue-50'
                          : formErrors.has('privacyPolicyAccepted')
                            ? 'border-red-500 bg-red-50/10'
                            : 'border-gray-200 bg-white'
                      }`}
                    >
                      <input
                        type="checkbox"
                        id="privacyPolicyAccepted"
                        checked={privacyPolicyAccepted}
                        disabled
                        className="sr-only"
                      />
                      <div
                        className={`flex-shrink-0 w-6 h-6 sm:w-5 sm:h-5 rounded border-2 flex items-center justify-center mt-0.5 sm:mt-0 ${privacyPolicyAccepted ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                      >
                        {privacyPolicyAccepted && (
                          <svg
                            className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-gray-700 leading-tight">
                        {t('consent.acceptPrivacy')}
                      </span>
                    </div>
                    {getErrorMessage('privacyPolicyAccepted') && (
                      <p className="mt-2 text-sm text-red-600">{getErrorMessage('privacyPolicyAccepted')}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row landscape:flex-row gap-2.5 sm:gap-3 pt-3 sm:pt-4 landscape:pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (window.confirm(t('dialogs.confirmCancel'))) {
                    navigate('/my-families');
                  }
                }}
                className="touch-feedback w-full sm:w-auto"
                disabled={isSubmitting}
              >
                {t('buttons.cancel')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClearDraft}
                className="touch-feedback w-full sm:w-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                disabled={isSubmitting}
              >
                {t('buttons.clearDraft')}
              </Button>
              <Button
                type="submit"
                variant="default"
                className="touch-feedback w-full sm:flex-1"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('buttons.saving') : t('buttons.continue')}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {showPrivacyModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black bg-opacity-40">
          <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col border border-gray-200">
            <div className="relative sticky top-0 z-10 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-b border-gray-200 p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <span className="text-3xl sm:text-4xl">🔒</span>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('privacyModal.title')}</h2>
                    <p className="text-sm text-gray-600 mt-0.5">{t('privacyModal.intro')}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(false)}
                  className="text-gray-400 hover:text-gray-600 active:text-gray-800 transition-colors p-2 min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation rounded-lg hover:bg-white/50"
                  aria-label={t('ariaLabels.close')}
                >
                  <X className="h-6 w-6 sm:h-5 sm:w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-5 md:p-6 bg-white">
              <div className="space-y-4 sm:space-y-5 text-sm sm:text-base text-gray-700">
                <div className="text-center pb-3 border-b border-gray-200">
                  <h3 className="font-semibold text-lg sm:text-xl text-gray-800">
                    {t('privacyModal.organizationName')}
                  </h3>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 sm:p-4 border border-blue-200">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-xl sm:text-2xl">📋</span>
                    <h4 className="font-semibold text-sm sm:text-base text-gray-800">
                      {t('privacyModal.infoWeCollect')}
                    </h4>
                  </div>
                  <p className="mb-2 text-gray-700">{t('privacyModal.weCollect')}</p>
                  <ul className="list-disc pl-5 sm:pl-6 space-y-1 text-gray-700">
                    <li>{t('privacyModal.parentDetails')}</li>
                    <li>{t('privacyModal.childInfo')}</li>
                    <li>{t('privacyModal.pregnancyStatus')}</li>
                    <li>{t('privacyModal.postalCodeIncome')}</li>
                    <li>{t('privacyModal.hearAboutUs')}</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 sm:p-4 border border-purple-200">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-xl sm:text-2xl">🎯</span>
                    <h4 className="font-semibold text-sm sm:text-base text-gray-800">{t('privacyModal.howWeUse')}</h4>
                  </div>
                  <p className="mb-2 text-gray-700">{t('privacyModal.weUseInfo')}</p>
                  <ul className="list-disc pl-5 sm:pl-6 space-y-1 text-gray-700">
                    <li>{t('privacyModal.improvePrograms')}</li>
                    <li>{t('privacyModal.reportFunders')}</li>
                    <li>{t('privacyModal.communicate')}</li>
                  </ul>
                  <div className="mt-3 p-3 bg-white/70 rounded border-l-4 border-purple-300">
                    <div className="flex items-start gap-2">
                      <span className="text-lg">🛡️</span>
                      <p className="text-gray-800 font-medium text-sm sm:text-base">{t('privacyModal.notShare')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-3 sm:p-4 border border-pink-200">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-xl sm:text-2xl">✅</span>
                    <h4 className="font-semibold text-sm sm:text-base text-gray-800">{t('privacyModal.yourRights')}</h4>
                  </div>
                  <ul className="list-disc pl-5 sm:pl-6 space-y-1 text-gray-700">
                    <li>{t('privacyModal.voluntary')}</li>
                    <li>{t('privacyModal.withdrawConsent')}</li>
                    <li>{t('privacyModal.accessCorrect')}</li>
                    <li>{t('privacyModal.secureStorage')}</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-3 sm:p-4 border border-blue-200">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-xl sm:text-2xl">🔐</span>
                    <h4 className="font-semibold text-sm sm:text-base text-gray-800">
                      {t('privacyModal.dataProtection')}
                    </h4>
                  </div>
                  <p className="text-gray-700">{t('privacyModal.canadianLegislation')}</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-3 sm:p-4 border border-purple-200">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-xl sm:text-2xl">📞</span>
                    <h4 className="font-semibold text-sm sm:text-base text-gray-800">{t('privacyModal.contactUs')}</h4>
                  </div>
                  <div className="space-y-1.5 text-gray-700">
                    <p>
                      <span className="font-medium text-gray-800">{t('privacyModal.phone')}</span>{' '}
                      <a href="tel:506-384-7874" className="text-blue-600 hover:text-blue-700 underline">
                        {t('privacyModal.phoneNumber')}
                      </a>
                    </p>
                    <p>
                      <span className="font-medium text-gray-800">{t('privacyModal.email')}</span>{' '}
                      <a href="mailto:admin@frc-crf.com" className="text-blue-600 hover:text-blue-700 underline">
                        {t('privacyModal.emailAddress')}
                      </a>
                    </p>
                    <p>
                      <span className="font-medium text-gray-800">{t('privacyModal.address')}</span>{' '}
                      <span>{t('privacyModal.physicalAddress')}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 p-3 sm:p-4 md:p-5 border-t border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50 sticky bottom-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPrivacyModal(false)}
                className="w-full sm:w-auto sm:min-w-[120px] min-h-[44px] touch-manipulation hover:bg-white"
              >
                {t('privacyModal.close')}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setPrivacyPolicyAccepted(true);
                  setShowPrivacyModal(false);
                }}
                className="w-full sm:w-auto sm:min-w-[120px] min-h-[44px] touch-manipulation bg-blue-500 hover:bg-blue-600 text-white"
              >
                {t('privacyModal.accept')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
